import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import * as Actions from './app.actions';

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
  AUTO = 'auto',
}

export interface AppStateModel {
  theme: Theme;
  systemTheme: Theme;
}

const getSystemTheme = (): Theme.LIGHT | Theme.DARK =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? Theme.DARK : Theme.LIGHT;

const defaults: AppStateModel = {
  theme: Theme.AUTO,
  systemTheme: getSystemTheme(),
};

type Model = AppStateModel;
type Context = StateContext<Model>;

@Injectable()
@State<Model>({
  name: 'app',
  defaults,
})
export class AppState implements NgxsOnInit {
  private document = inject(DOCUMENT);
  private mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private systemThemeListener: ((event: MediaQueryListEvent) => void) | null = null;

  ngxsOnInit(ctx: Context): void {
    const saved = localStorage.getItem('theme') as Theme | null;
    this.applyTheme(saved ?? Theme.AUTO, ctx);
  }

  @Selector()
  static theme(state: Model): Theme {
    return state.theme;
  }

  @Action(Actions.SetTheme)
  setTheme(ctx: Context, { theme }: Actions.SetTheme) {
    this.applyTheme(theme, ctx);
  }

  private applyTheme(theme: Theme, ctx: Context): void {
    if (theme === Theme.AUTO) {
      this.attachSystemListener(ctx);
      const resolved = getSystemTheme();
      this.applyBodyClasses(resolved);
      ctx.patchState({ theme: Theme.AUTO, systemTheme: resolved });
    } else {
      this.detachSystemListener();
      this.applyBodyClasses(theme);
      ctx.patchState({ theme, systemTheme: getSystemTheme() });
    }
    localStorage.setItem('theme', theme);
  }

  private applyBodyClasses(resolved: Theme.LIGHT | Theme.DARK): void {
    this.document.body.classList.toggle('light', resolved === Theme.LIGHT);
    this.document.body.classList.toggle('dark', resolved === Theme.DARK);
  }

  private attachSystemListener(ctx: Context): void {
    if (this.systemThemeListener) return;
    this.systemThemeListener = (event: MediaQueryListEvent) => {
      const resolved = event.matches ? Theme.DARK : Theme.LIGHT;
      this.applyBodyClasses(resolved);
      ctx.patchState({ systemTheme: resolved });
    };
    this.mediaQuery.addEventListener('change', this.systemThemeListener);
  }

  private detachSystemListener(): void {
    if (!this.systemThemeListener) return;
    this.mediaQuery.removeEventListener('change', this.systemThemeListener);
    this.systemThemeListener = null;
  }
}